import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, Inbox, Plus, Check, X, Trophy, Phone, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { EmptyState, fmtMoney } from './shared';
import { ReferralDialog, ConcludeDialog } from './ReferralDialogs';
import { ReferralThreadDialog } from './ReferralThread';

const STATUS = {
  envoye: ['Envoyé', 'bg-sky-100 text-sky-700'],
  accepte: ['Pris en charge', 'bg-amber-100 text-amber-700'],
  refuse: ['Refusé', 'bg-gray-100 text-gray-600'],
  conclu: ['Conclu', 'bg-emerald-100 text-emerald-700'],
};

const ReferralRow = ({ r, isAdmin, onStatus, onConclude, onThread }) => {
  const [label, cls] = STATUS[r.status] || [r.status, 'bg-gray-100'];
  const received = r.direction === 'received';
  const open = r.status === 'envoye' || r.status === 'accepte';
  const canManage = isAdmin && open && (received || r.external);
  const myShare = received ? r.receiver_share : r.referrer_share;
  return (
    <div className="bg-white rounded-2xl border border-[#B85C38]/15 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`referral-row-${r.id}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${received ? 'bg-[#2B3A2C] text-white' : 'bg-[#B85C38]/10 text-[#B85C38]'}`}>
        {received ? <Inbox className="w-5 h-5" /> : <Send className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-[#2B3A2C]">{r.client_name}</p>
          <Badge className={cls}>{label}</Badge>
          <span className="text-[11px] text-gray-500 capitalize">{r.need}{r.budget ? ` · ${fmtMoney(r.budget)}` : ''}</span>
          {r.external && <Badge variant="outline" className="text-[10px]">Hors plateforme</Badge>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {received ? `De ${r.from_agency_name}` : `Vers ${r.to_agency_name}`}
          {r.property_title ? ` · bien : ${r.property_title}` : ''} · {new Date(r.created_at).toLocaleDateString('fr-FR')}
        </p>
        <p className="text-xs text-gray-600 flex gap-3 mt-0.5">
          {r.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.client_phone}</span>}
          {r.client_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.client_email}</span>}
        </p>
        {r.notes && <p className="text-xs text-gray-500 italic mt-0.5">{r.notes}</p>}
        {isAdmin && r.status === 'conclu' && (
          <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid={`referral-share-${r.id}`}>
            Commission {fmtMoney(r.commission_amount)} · votre part : {fmtMoney(myShare)}
          </p>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={() => onThread(r)} className="rounded-xl border-[#2B3A2C]/25" data-testid={`referral-thread-${r.id}`}>
        <MessageCircle className="w-4 h-4 mr-1" /> Discussion{r.messages_count ? ` (${r.messages_count})` : ''}
      </Button>
      {canManage && (
        <div className="flex gap-2 flex-wrap">
          {r.status === 'envoye' && (
            <Button size="sm" variant="outline" onClick={() => onStatus(r, 'accepte')} className="rounded-xl" data-testid={`referral-accept-${r.id}`}>
              <Check className="w-4 h-4 mr-1" /> Prendre en charge
            </Button>
          )}
          <Button size="sm" onClick={() => onConclude(r)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid={`referral-conclude-${r.id}`}>
            <Trophy className="w-4 h-4 mr-1" /> Conclu
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onStatus(r, 'refuse')} className="rounded-xl text-gray-500" data-testid={`referral-refuse-${r.id}`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export const ReferralsTab = ({ isAdmin, active }) => {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [concluding, setConcluding] = useState(null);
  const [thread, setThread] = useState(null);
  const load = useCallback(() => {
    api.get('/partnerships/referrals').then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const earned = useMemo(() => rows.filter((r) => r.status === 'conclu')
    .reduce((s, r) => s + ((r.direction === 'received' ? r.receiver_share : r.referrer_share) || 0), 0), [rows]);

  const onStatus = async (r, status) => {
    try {
      await api.put(`/partnerships/referrals/${r.id}/status`, { status });
      toast.success(status === 'refuse' ? 'Recommandation refusée' : 'Client pris en charge'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        {isAdmin && (
          <Button onClick={() => setCreating(true)} disabled={!active.length} className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="referral-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Recommander un client
          </Button>
        )}
        {isAdmin && (
          <div className="rounded-xl bg-[#2B3A2C] text-[#F5EFE1] px-4 py-2 text-sm" data-testid="referrals-earned-total">
            Commissions partagées gagnées : <b>{fmtMoney(earned)}</b>
          </div>
        )}
      </div>
      {rows.length === 0
        ? <EmptyState icon={Send} text="Aucun client recommandé pour le moment." testid="referrals-empty" />
        : rows.map((r) => <ReferralRow key={r.id} r={r} isAdmin={isAdmin} onStatus={onStatus} onConclude={setConcluding} onThread={setThread} />)}
      <ReferralThreadDialog referral={thread} onClose={() => setThread(null)} onPosted={load} />
      <ReferralDialog open={creating} onClose={() => setCreating(false)} active={active} onDone={load} />
      <ConcludeDialog referral={concluding} onClose={() => setConcluding(null)} onDone={load} />
    </div>
  );
};
